import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getJobs, getOrders } from '../redux/actions/job.action';
import PlatformAvatar from '../components/platform/PlatformAvatar';
import PlatformButton from '../components/platform/PlatformButton';
import PlatformIcon from '../components/platform/PlatformIcon';
import '../styles/platform-dashboard.css';

const ROWS_PER_PAGE = 8;

const getUserName = (user) =>
  user.fullName
  || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null)
  || user.displayName
  || user.name
  || user.username
  || user.title
  || user.email
  || 'Unknown';

const getUserDate = (user) => {
  if (user.registeredOn && typeof user.registeredOn !== 'string') {
    return new Date(user.registeredOn.seconds * 1000).toLocaleDateString();
  }

  if (user.accountCreated && typeof user.accountCreated !== 'string') {
    return new Date(user.accountCreated.seconds * 1000).toLocaleDateString();
  }

  if (typeof user.accountCreated === 'string') {
    return user.accountCreated;
  }

  return user.fulldate || '—';
};

export default function UserListPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobs, loading } = useSelector((state) => state.jobs);
  const { user } = useSelector((state) => state.auth || {});

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(getOrders());
    dispatch(getJobs());
  }, [dispatch]);

  const users = useMemo(() => {
    let list = jobs || [];

    if (user?.companyID && list.length > 0) {
      list = list.filter((job) => job.companyID === user.companyID);
    }

    if (search.trim()) {
      const query = search.trim().toLowerCase();
      list = list.filter((item) => {
        const name = getUserName(item).toLowerCase();
        const email = (item.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }

    return list;
  }, [jobs, user?.companyID, search]);

  const pageCount = Math.max(1, Math.ceil(users.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedUsers = users.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const handleViewUser = (userRecord) => {
    const userId = userRecord.uid || userRecord.id;
    if (!userId) {
      return;
    }

    navigate('/dashboard/student-stats/', { state: { id: userId } });
  };

  return (
    <div className="nurturer-platform">
      <div className="platform-page platform-fade">
        <div className="platform-row users-toolbar">
          <div className="platform-row users-search">
            <PlatformIcon name="search" size={16} />
            <input
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <PlatformButton variant="primary" size="md" icon="plus" onClick={() => navigate('/dashboard/users/create')}>
            Add user
          </PlatformButton>
        </div>

        {loading ? (
          <div className="platform-card users-loading">Loading users…</div>
        ) : (
          <div className="platform-card users-table-card">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="hide-mobile">Email</th>
                  <th>Joined</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--muted)', padding: '32px 18px' }}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  pagedUsers.map((item) => {
                    const name = getUserName(item);
                    return (
                      <tr key={item.id || item.uid || item.email} className="users-table-row">
                        <td>
                          <div className="platform-row" style={{ gap: 12 }}>
                            <PlatformAvatar name={name} size={36} photoUrl={item.photoUrl} />
                            <span style={{ fontWeight: 600 }}>{name}</span>
                          </div>
                        </td>
                        <td className="hide-mobile">{item.email || '—'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)' }}>
                          {getUserDate(item)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <PlatformButton
                            variant="soft"
                            size="sm"
                            onClick={() => handleViewUser(item)}
                          >
                            View
                          </PlatformButton>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {users.length > ROWS_PER_PAGE && (
              <div className="platform-row users-pagination">
                <span className="users-pagination-meta">
                  Showing {safePage * ROWS_PER_PAGE + 1}–{Math.min((safePage + 1) * ROWS_PER_PAGE, users.length)} of {users.length}
                </span>
                <div className="platform-row" style={{ gap: 8 }}>
                  <PlatformButton
                    variant="outline"
                    size="sm"
                    icon="chevL"
                    disabled={safePage === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    Previous
                  </PlatformButton>
                  <PlatformButton
                    variant="outline"
                    size="sm"
                    disabled={safePage >= pageCount - 1}
                    onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                  >
                    Next
                    <PlatformIcon name="chevR" size={14} />
                  </PlatformButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
